import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Box, styled } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../Loader/FullScreenLoader";
import { useAuth } from "../../context/AuthProvider";
import dayjs from "dayjs";
import "./RoomLogArchive.css";

const RoomLogArchive = ({ roomId, harvestedDate }) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [loading, setLoading] = useState(false);
  const [archiveData, setArchiveData] = useState([]);

  const dateFilterData = JSON.parse(
    localStorage.getItem("roomLogArchiveDateFilter") || "null",
  );

  const hasValidStoredStart =
    dateFilterData?.startDate && dayjs(dateFilterData.startDate).isValid();
  const hasValidStoredEnd =
    dateFilterData?.endDate && dayjs(dateFilterData.endDate).isValid();

  const [startDate, setStartDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.startDate : "",
  );
  const [endDate, setEndDate] = useState(
    hasValidStoredStart && hasValidStoredEnd ? dateFilterData.endDate : "",
  );

  // Ensure end date is never before start date
  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(endDate).isValid() &&
      dayjs(startDate).isValid() &&
      dayjs(endDate).isBefore(dayjs(startDate), "day")
    ) {
      const clearedEnd = "";
      setEndDate(clearedEnd);
      const roomLogArchiveDateFilter = {
        startDate,
        endDate: clearedEnd,
      };
      localStorage.setItem(
        "roomLogArchiveDateFilter",
        JSON.stringify(roomLogArchiveDateFilter),
      );
    }
  }, [startDate]);

  const getColorStyle = (color) => {
    const colors = {
      RED: "#ffebee",
      YELLOW: "#fff9c4",
      GREEN: "#e8f5e9",
    };
    return colors[color] || "transparent";
  };

  const fetchArchivedData = async () => {
    setLoading(true);
    try {
      let endpoint = `room-logs/archive/${roomId}?harvested_date=${harvestedDate}`;
      const params = [];

      const hasValidStart = !!startDate && dayjs(startDate).isValid();
      const hasValidEnd = !!endDate && dayjs(endDate).isValid();

      if (hasValidStart && hasValidEnd) {
        params.push(`start_date=${startDate}`);
        params.push(`end_date=${endDate}`);
      }

      if (params.length > 0) {
        endpoint += `?${params.join("&")}`;
      }

      const response = await httpService.get(endpoint);
      if (response?.data) {
        const mapped = Array.isArray(response.data)
          ? response.data.map((row) => ({
              id: row.id,
              growth_phase: row.growth_phase_name,
              growth_phase_id: row.growth_phase_id,
              day_temp_target: parseFloat(row.day_temp_target) || 0,
              avg_day_temp: parseFloat(row.average_day_temp) || 0,
              avg_day_temp_color: row.average_day_temp_color,
              night_temp_target: parseFloat(row.night_temp_target) || 0,
              avg_night_temp: parseFloat(row.average_night_temp) || 0,
              avg_night_temp_color: row.average_night_temp_color,
              day_humidity_target: parseFloat(row.day_humidity_target) || 0,
              avg_day_humidity: parseFloat(row.average_day_humidity) || 0,
              avg_day_humidity_color: row.average_day_humidity_color,
              night_humidity_target: parseFloat(row.night_humidity_target) || 0,
              avg_night_humidity: parseFloat(row.average_night_humidity) || 0,
              avg_night_humidity_color: row.average_night_humidity_color,
              ph_target: parseFloat(row.ph_target) || 0,
              epi01_ph_avg: parseFloat(row.epi_01_ph_avg) || 0,
              epi01_ph_avg_color: row.epi_01_ph_avg_color,
              epi02_ph_avg: parseFloat(row.epi_02_ph_avg) || 0,
              epi02_ph_avg_color: row.epi_02_ph_avg_color,
              epi03_ph_avg: parseFloat(row.epi_03_ph_avg) || 0,
              epi03_ph_avg_color: row.epi_03_ph_avg_color,
              tds_target: parseFloat(row.tds_target) || 0,
              epi01_tds_avg: parseFloat(row.epi_01_tds_avg) || 0,
              epi01_tds_avg_color: row.epi_01_tds_avg_color,
              epi02_tds_avg: parseFloat(row.epi_02_tds_avg) || 0,
              epi02_tds_avg_color: row.epi_02_tds_avg_color,
              epi03_tds_avg: parseFloat(row.epi_03_tds_avg) || 0,
              epi03_tds_avg_color: row.epi_03_tds_avg_color,
              water_temp_target: parseFloat(row.water_temp_target) || 0,
              epi01_water_temp_avg: parseFloat(row.epi_01_water_temp_avg) || 0,
              epi01_water_temp_avg_color: row.epi_01_water_temp_avg_color,
              epi02_water_temp_avg: parseFloat(row.epi_02_water_temp_avg) || 0,
              epi02_water_temp_avg_color: row.epi_02_water_temp_avg_color,
              epi03_water_temp_avg: parseFloat(row.epi_03_water_temp_avg) || 0,
              epi03_water_temp_avg_color: row.epi_03_water_temp_avg_color,
              avg_light_level: parseFloat(row.average_light_level_avg) || 0,
              co2_target: parseFloat(row.co2_target) || 0,
              avg_co2: parseFloat(row.average_co2_avg) || 0,
              avg_co2_color: row.average_co2_avg_color,
              planted_date: row.planted_date
                ? dayjs(row.planted_date).format("DD-MM-YYYY")
                : "",
              harvested_date: row.harvested_date
                ? dayjs(row.harvested_date).format("DD-MM-YYYY")
                : "",
              note: row.note || "",
              archived_date: row.archived_at
                ? dayjs(row.archived_at).format("DD-MM-YYYY")
                : "",
            }))
          : [];
        setArchiveData(mapped);
      }
    } catch (error) {
      setArchiveData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roomId) {
      fetchArchivedData();
    }
  }, [roomId, startDate, endDate]);

  const StyledGridOverlay = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }));

  const CustomNoRowsOverlay = () => (
    <StyledGridOverlay>
      <Box sx={{ mt: 1 }}>No Data Found</Box>
    </StyledGridOverlay>
  );

  const columns = [
    {
      field: "growth_phase",
      headerName: "Growth Phase",
      minWidth: 150,
    },
    {
      field: "day_temp_target",
      headerName: "Day Temp Target",
      minWidth: 160,
    },
    {
      field: "avg_day_temp",
      headerName: "Average Day Temp",
      minWidth: 170,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.avg_day_temp_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "night_temp_target",
      headerName: "Night Temp Target",
      minWidth: 180,
    },
    {
      field: "avg_night_temp",
      headerName: "Average Night Temp",
      minWidth: 190,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.avg_night_temp_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "day_humidity_target",
      headerName: "Day Humidity Target",
      minWidth: 190,
    },
    {
      field: "avg_day_humidity",
      headerName: "Average Day Humidity",
      minWidth: 210,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.avg_day_humidity_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "night_humidity_target",
      headerName: "Night Humidity Target",
      minWidth: 210,
    },
    {
      field: "avg_night_humidity",
      headerName: "Average Night Humidity",
      minWidth: 230,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.avg_night_humidity_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "ph_target",
      headerName: "PH Target",
      minWidth: 120,
    },
    {
      field: "epi01_ph_avg",
      headerName: "Epi 01 pH Avg",
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi01_ph_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi02_ph_avg",
      headerName: "Epi 02 pH Avg",
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi02_ph_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi03_ph_avg",
      headerName: "Epi 03 pH Avg",
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi03_ph_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "tds_target",
      headerName: "TDS Target",
      minWidth: 130,
    },
    {
      field: "epi01_tds_avg",
      headerName: "Epi 01 TDS Avg",
      minWidth: 160,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi01_tds_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi02_tds_avg",
      headerName: "Epi 02 TDS Avg",
      minWidth: 160,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi02_tds_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi03_tds_avg",
      headerName: "Epi 03 TDS Avg",
      minWidth: 160,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.epi03_tds_avg_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "water_temp_target",
      headerName: "Water Temp Target",
      minWidth: 180,
    },
    {
      field: "epi01_water_temp_avg",
      headerName: "Epi 01 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(
              params.row.epi01_water_temp_avg_color,
            ),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi02_water_temp_avg",
      headerName: "Epi 02 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(
              params.row.epi02_water_temp_avg_color,
            ),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "epi03_water_temp_avg",
      headerName: "Epi 03 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(
              params.row.epi03_water_temp_avg_color,
            ),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "avg_light_level",
      headerName: "Average Light Level",
      minWidth: 190,
    },
    {
      field: "co2_target",
      headerName: "CO2 Target",
      minWidth: 130,
    },
    {
      field: "avg_co2",
      headerName: "Average CO2",
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: getColorStyle(params.row.avg_co2_color),
            padding: "8px",
            width: "100%",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "planted_date",
      headerName: "Planted Date",
      minWidth: 150,
    },
    {
      field: "harvested_date",
      headerName: "Harvested Date",
      minWidth: 150,
    },
    {
      field: "archived_date",
      headerName: "Archived Date",
      minWidth: 150,
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}

      <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
        {/* <div className="header_search-wrap">
          <div className="header_search_bar common_content">
            <div
              className="input-group input_group"
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                backgroundColor: "unset",
                paddingBottom: "12px",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate ? dayjs(startDate) : null}
                  disableFuture
                  onChange={(newStartDate) => {
                    if (!newStartDate) {
                      setStartDate("");
                      const roomLogArchiveDateFilter = {
                        startDate: "",
                        endDate,
                      };
                      localStorage.setItem(
                        "roomLogArchiveDateFilter",
                        JSON.stringify(roomLogArchiveDateFilter),
                      );
                      return;
                    }

                    const formattedDate =
                      dayjs(newStartDate).format("YYYY-MM-DD");
                    setStartDate(formattedDate);
                    const roomLogArchiveDateFilter = {
                      startDate: formattedDate,
                      endDate,
                    };
                    localStorage.setItem(
                      "roomLogArchiveDateFilter",
                      JSON.stringify(roomLogArchiveDateFilter),
                    );
                  }}
                  format="YYYY-MM-DD"
                  sx={{ minWidth: "200px", backgroundColor: "white" }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate ? dayjs(endDate) : null}
                  disableFuture
                  minDate={
                    startDate && dayjs(startDate).isValid()
                      ? dayjs(startDate)
                      : undefined
                  }
                  onChange={(newEndDate) => {
                    if (!newEndDate) {
                      setEndDate("");
                      const roomLogArchiveDateFilter = {
                        startDate,
                        endDate: "",
                      };
                      localStorage.setItem(
                        "roomLogArchiveDateFilter",
                        JSON.stringify(roomLogArchiveDateFilter),
                      );
                      return;
                    }

                    const formattedDate =
                      dayjs(newEndDate).format("YYYY-MM-DD");
                    setEndDate(formattedDate);
                    const roomLogArchiveDateFilter = {
                      startDate,
                      endDate: formattedDate,
                    };
                    localStorage.setItem(
                      "roomLogArchiveDateFilter",
                      JSON.stringify(roomLogArchiveDateFilter),
                    );
                  }}
                  format="YYYY-MM-DD"
                  sx={{ minWidth: "200px", backgroundColor: "white" }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div> */}
      </div>

      <div
        className="room-log-archive-wrapper"
        style={{
          height: `${archiveData?.length > 0 ? "" : "200px"}`,
          width: "100%",
        }}
      >
        {/* Fake frozen header */}
        <div className="fake-frozen-header">
          <div className="fake-header-cell" style={{ width: 150 }}>
            Growth Phase
          </div>
        </div>

        <DataGrid
          rows={archiveData}
          columns={columns}
          loading={loading}
          autoHeight
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 1 },
          }}
          columnBuffer={columns.length}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          hideFooter
          showCellVerticalBorder
          showColumnVerticalBorder
        />
      </div>

      {/* Notes Table Section */}
      <div style={{ marginTop: 24 }}>
        <DataGrid
          rows={archiveData}
          columns={[
            {
              field: "growth_phase",
              headerName: "Growth Phase",
              minWidth: 150,
              renderCell: (params) => (
                <div style={{ padding: "8px" }}>{params.value || "-"}</div>
              ),
            },
            {
              field: "note",
              headerName: "Note",
              flex: 1,
              minWidth: 300,
              renderCell: (params) => (
                <div style={{ padding: "8px" }}>{params.value || "-"}</div>
              ),
            },
          ]}
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: "1px solid #E0E0E0",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#EEF0FF",
              fontWeight: 600,
            },
            [`& .${gridClasses.cell}`]: { py: 0.5 },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
          hideFooter
        />
      </div>
    </>
  );
};

export default RoomLogArchive;
